import { useEffect, useState } from 'react';
import { Button } from '@rocco/components/button';
import {
    countriesList,
    useCountryContext,
} from '@archipro-website/localisation';
import { useLocalStorageState } from 'ahooks';
import { useTracker } from '@archipro-website/tracker';
import { useRootData } from '~/modules/root/hook/use-root-data';
import type { HeaderCountryElement } from '~/utils/country-switch';
import { CountryFlag } from './CountryFlag';
import { getCountrySuggestion } from './get-country-suggestion';

interface CountrySuggestModalProps {
    countries: HeaderCountryElement[];
}

const CountrySuggestModal = ({ countries }: CountrySuggestModalProps) => {
    const { code: siteCountryCode } = useCountryContext();
    const { visitorCountry } = useRootData();
    const [suggestion, setSuggestion] = useState<HeaderCountryElement | null>(
        null,
    );
    const [pickedCountryCode, setPickedCountryCode] =
        useLocalStorageState<string>('ap_set_country_code', {
            defaultValue: '',
            serializer: (v) => v ?? '',
            deserializer: (v) => v,
        });
    const tracker = useTracker();

    // Decided after mount: the "No thanks" choice lives in localStorage, which
    // the server render cannot see.
    useEffect(() => {
        setSuggestion(
            getCountrySuggestion({
                siteCountry: siteCountryCode,
                visitorCountry,
                pickedCountry: pickedCountryCode ?? '',
                countries,
            }),
        );
    }, [siteCountryCode, visitorCountry, pickedCountryCode, countries]);

    if (!suggestion || !visitorCountry) {
        return null;
    }

    const logClick = (answer: 'Yes' | 'No') => {
        tracker.log('CountryModalClick', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
            data: {
                ExtraData: answer,
            },
        });
    };

    const onConfirm = () => {
        logClick('Yes');
        window.location.href = suggestion.url;
    };

    const onCancel = () => {
        logClick('No');
        setPickedCountryCode(siteCountryCode);
        setSuggestion(null);
    };

    const { nameCasual, domain } = countriesList[visitorCountry];

    // One above the z-fixed chat dock it shares the corner with, and still
    // below dialogs (z-modalBackdrop and up).
    return (
        <div className="fixed right-6 bottom-[90px] z-[121] w-[calc(100vw-48px)] bg-white p-7 shadow-[0_4px_24px_rgba(0,0,0,0.2)] md:right-12 md:bottom-12 md:w-[450px]">
            <CountryFlag
                country={visitorCountry}
                className="absolute top-7 right-7 h-6"
            />
            <p className="mb-6 pr-12 text-2xl leading-tight font-medium text-gray-900">
                It seems like you&apos;re in{' '}
                <span className="whitespace-nowrap">{nameCasual}</span>.
            </p>
            <p className="mb-10 text-base leading-normal text-gray-700">
                Would you like to go to{' '}
                <a
                    href={suggestion.url}
                    onClick={(event) => {
                        event.preventDefault();
                        onConfirm();
                    }}
                    className="underline"
                >
                    {domain}
                </a>
                ?
            </p>
            <div className="flex gap-3">
                <Button color="outline" size="larger" onClick={onCancel}>
                    No thanks
                </Button>
                <Button color="dark" size="larger" onClick={onConfirm}>
                    Yes please
                </Button>
            </div>
        </div>
    );
};

export default CountrySuggestModal;
